import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { orderAPI } from '../../services/api';

const CancelOrderDialog = ({ open, onClose, order, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [allocatedSkus, setAllocatedSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open && order) {
      setReason('');
      setError('');
      fetchAllocatedSkus();
    }
  }, [open, order]);

  const fetchAllocatedSkus = async () => {
    if (!order?.id) return;
    
    setLoadingSkus(true);
    try {
      const response = await orderAPI.getAllocatedSkus(order.id);
      if (response.success) {
        setAllocatedSkus(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch allocated SKUs:', err);
    } finally {
      setLoadingSkus(false);
    }
  };

  const handleCancel = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await orderAPI.cancelOrder(order.id, reason.trim());
      if (response.success) {
        onConfirm(response);
        onClose();
      } else {
        setError(response.message || 'Failed to cancel order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while cancelling the order');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return '#f59e0b';
      case 'CONFIRMED': return '#3b82f6';
      case 'PROCESSING': return '#8b5cf6';
      case 'PICKING': return '#6366f1';
      case 'SHIPPED': return '#10b981';
      default: return '#6b7280';
    }
  };

  const pickedCount = order?.items?.reduce((sum, item) => sum + (item.pickedQuantity || 0), 0) || 0;
  const allocatedCount = allocatedSkus.length;

  return (
    <Dialog 
      open={open} 
      onClose={submitting ? undefined : onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderTop: '4px solid #ef4444'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444' }}>
        <CancelIcon />
        Cancel Order {order?.orderNumber}
      </DialogTitle>
      <DialogContent>
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ mb: 2, mt: 1 }}
        >
          <Typography variant="body2" fontWeight={500}>
            This action cannot be undone.
          </Typography>
          <Typography variant="body2">
            Cancelling this order will update its status to CANCELLED and restore all allocated tissue IDs back to available inventory.
          </Typography>
        </Alert>
        
        {/* Order Info */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Order Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <Chip 
                  label={order?.status} 
                  size="small" 
                  sx={{ 
                    bgcolor: getStatusColor(order?.status), 
                    color: 'white',
                    fontWeight: 600 
                  }} 
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Client</Typography>
              <Typography variant="body2" fontWeight={500}>
                {order?.user?.firstName} {order?.user?.lastName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="body2" fontWeight={500}>
                ${Number(order?.totalAmount || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Inventory Impact */}
        {(pickedCount > 0 || allocatedCount > 0) && (
          <Alert 
            severity="info" 
            icon={<InventoryIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight={500}>
              Inventory will be restored
            </Typography>
            <Typography variant="body2">
              {allocatedCount > 0 
                ? `${allocatedCount} tissue ID(s) will be returned to available inventory.`
                : `${pickedCount} picked item(s) will have their allocation cleared.`
              }
            </Typography>
          </Alert>
        )}

        {/* Show allocated SKUs if any */}
        {loadingSkus ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : allocatedSkus.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Allocated Tissue IDs to be restored:
            </Typography>
            <Box sx={{ 
              maxHeight: 150, 
              overflow: 'auto', 
              border: '1px solid #e2e8f0', 
              borderRadius: 1,
              p: 1
            }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {allocatedSkus.map((sku, idx) => (
                  <Chip
                    key={sku.id || idx}
                    label={sku.tissueId || sku.sku}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Reason Input */}
        <TextField
          label="Cancellation Reason *"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
          placeholder="Please provide a reason for cancelling this order..."
          error={!!error}
          helperText={error || 'This reason will be recorded in the order history.'}
          disabled={submitting}
          sx={{ mt: 1 }}
          InputProps={{
            sx: { fontFamily: 'inherit' }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={submitting}
          variant="outlined"
        >
          Keep Order
        </Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          color="error"
          disabled={submitting || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
        >
          {submitting ? 'Cancelling...' : 'Cancel Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelOrderDialog;

